import styles from './style.module.scss';
import {
  CardStackData,
  CardStackItem,
} from 'tenants';
import Image from 'components/image-loader/image';

import { useTranslation } from 'next-i18next';

type CardProps = {
  index: number;
  card: CardStackItem;
};

function Card({ card, index }: CardProps) {
  const { t } = useTranslation();

  // Derive CTA border/divider colour from the text colour — white text = dark
  // card (use white-tint border), dark text = light card (use navy-tint border).
  const isLightText = card.textColor === '#FFF' || card.textColor === '#fff' || card.textColor === 'white';
  const ctaBorderColor = isLightText ? 'rgba(255,255,255,.4)' : 'rgba(15,26,51,.25)';

  return (
    <div
      key={card.title}
      className={styles.card}
      style={{ '--index': index, backgroundColor: card.backgroundColor, color: card.textColor } as React.CSSProperties}>
      <div className={styles.cardRow}>
        <div className={styles.leftSection}>
          <div className={styles.cardNumber}>{`0${index + 1}`}</div>
        </div>
        <div className={styles.middleSection}>
          <h3>{t(card.title)}</h3>
          <p>{t(card.description)}</p>
          {card.href && (
            <div className={styles.cardCta}>
              <a
                href={card.href}
                target="_blank"
                rel="noopener noreferrer"
                style={{ borderColor: ctaBorderColor, color: 'inherit' }}
              >
                <span className={styles.cardCtaLabel} style={{ borderRightColor: ctaBorderColor }}>
                  {card.ctaLabel ?? 'Learn more'}
                </span>
                <span className={styles.cardCtaArrow}>
                  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M3 8h10M8 3l5 5-5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </a>
            </div>
          )}
        </div>
        {card.imageSrc && (
          <div className={styles.rightSection}>
            <div className={styles.imageInner}>
              <Image
                src={card.imageSrc}
                alt={t(card.imageAlt)}
                isVisible={!!card.imageSrc}
                style={{ objectPosition: card.imagePosition ?? 'center' }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export type CardStackProps = {
  stack: CardStackData;
  /**
   * When true, the sticky section heading is not rendered and the card list
   * uses zero top-margin (no compensation for the heading's document space).
   * Use this when the page renders its own heading above the stack and wants
   * the first card — not the heading — to be the sticky element.
   */
  hideHeader?: boolean;
};

function CardStack({ stack, hideHeader = false }: CardStackProps) {
  const { t } = useTranslation();
  return (
    <div className={styles.wrapper}>
      {!hideHeader && (
        <div className={styles.header}>
          <h2>{t(stack.title)}</h2>
        </div>
      )}
      <div className={hideHeader ? styles.componentNoHeader : styles.component}>
        {stack.content.map((card, index) => (
          <Card key={card.title} card={card} index={index} />
        ))}
      </div>
    </div>
  );
}

export default CardStack;
